import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, AlertTriangle, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { productAPI } from '../services/api';
import { useI18n } from '../contexts/I18nContext';

interface Product {
  id: string;
  name: string;
  brand: string;
  description: string;
  unit: string;
  quantity: number;
  min_stock: number;
  cost_price: number;
  category: string;
  is_active: boolean;
  created_at: string;
}

const UNITS = [
  { value: 'L', label: 'Litros (L)' },
  { value: 'ml', label: 'Mililitros (ml)' },
  { value: 'kg', label: 'Quilogramas (kg)' },
  { value: 'g', label: 'Gramas (g)' },
  { value: 'unit', label: 'Unidade' },
  { value: 'pack', label: 'Pacote' },
  { value: 'box', label: 'Caixa' }
];

const CATEGORIES = [
  'Limpeza Geral',
  'Desinfecção',
  'Aromatizante',
  'Cera e Polimento',
  'Vidros',
  'Banheiro',
  'Cozinha',
  'Piso',
  'Móveis',
  'Outros'
];

const Produtos: React.FC = () => {
  const { t, formatCurrency } = useI18n();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adjustingProduct, setAdjustingProduct] = useState<Product | null>(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null); // Por padrão, mostrar todos
  const [showLowStock, setShowLowStock] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    description: '',
    unit: 'unit',
    quantity: 0,
    min_stock: 0,
    cost_price: 0,
    category: 'Limpeza Geral'
  });
  const [stockAdjustment, setStockAdjustment] = useState({
    quantity: 0,
    type: 'add',
    reason: ''
  });

  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterActive !== null) params.append('is_active', filterActive.toString());
      if (showLowStock) params.append('low_stock', 'true');
      
      const queryString = params.toString();
      const url = queryString ? `/products?${queryString}` : '/products';
      const response = await productAPI.get(url);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      alert(t('products.error.loading') || 'Erro ao carregar produtos');
    }
    setIsLoading(false);
  };

  const createProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await productAPI.post('/products', formData);
      setShowForm(false);
      resetForm();
      await loadProducts();
      alert(t('success') || 'Produto criado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao criar produto:', error);
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao criar produto';
      alert(errorMsg);
    }
    setIsLoading(false);
  };

  const updateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;
    
    setIsLoading(true);
    try {
      await productAPI.put(`/products/${editingProduct.id}`, formData);
      setEditingProduct(null);
      resetForm();
      await loadProducts();
      alert(t('success') || 'Produto atualizado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar produto:', error);
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao atualizar produto';
      alert(errorMsg);
    }
    setIsLoading(false);
  };

  const openDeleteModal = (product: Product) => {
    setDeletingProduct(product);
    setShowDeleteModal(true);
  };

  const softDeleteProduct = async () => {
    if (!deletingProduct) return;
    
    setIsLoading(true);
    try {
      await productAPI.put(`/products/${deletingProduct.id}`, { is_active: false });
      
      setShowDeleteModal(false);
      setDeletingProduct(null);
      setProducts([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadProducts();
      alert('Produto desativado com sucesso! Você pode reativá-lo a qualquer momento.');
    } catch (error) {
      console.error('Erro ao desativar produto:', error);
      alert(t('products.errorDeactivate'));
      await loadProducts();
    }
    setIsLoading(false);
  };

  const hardDeleteProduct = async () => {
    if (!deletingProduct) return;
    
    setIsLoading(true);
    try {
      await productAPI.delete(`/products/${deletingProduct.id}/hard`);
      
      setShowDeleteModal(false);
      setDeletingProduct(null);
      setProducts([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadProducts();
      alert('Produto deletado permanentemente com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar produto:', error);
      alert(t('services.hardDeleteUnavailable'));
      await softDeleteProduct();
    }
    setIsLoading(false);
  };

  const reactivateProduct = async (productId: string) => {
    if (!confirm('Tem certeza que deseja reativar este produto?')) return;
    
    try {
      await productAPI.put(`/products/${productId}`, { is_active: true });
      
      setProducts([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadProducts();
      alert('Produto reativado com sucesso!');
    } catch (error) {
      console.error('Erro ao reativar produto:', error);
      alert('Erro ao reativar produto');
      await loadProducts();
    }
  };

  const adjustStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustingProduct) return;

    setIsLoading(true);
    try {
      await productAPI.post(`/products/${adjustingProduct.id}/adjust-stock`, stockAdjustment);
      setShowStockModal(false);
      setAdjustingProduct(null);
      setStockAdjustment({ quantity: 0, type: 'add', reason: '' });
      await loadProducts();
      alert(t('success') || 'Estoque ajustado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao ajustar estoque:', error);
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao ajustar estoque';
      alert(errorMsg);
    }
    setIsLoading(false);
  };

  const startEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      brand: product.brand,
      description: product.description,
      unit: product.unit,
      quantity: product.quantity,
      min_stock: product.min_stock,
      cost_price: product.cost_price,
      category: product.category
    });
    setShowForm(true);
  };

  const openStockModal = (product: Product) => {
    setAdjustingProduct(product);
    setStockAdjustment({ quantity: 0, type: 'add', reason: '' });
    setShowStockModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      description: '',
      unit: 'unit',
      quantity: 0,
      min_stock: 0,
      cost_price: 0,
      category: 'Limpeza Geral'
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  useEffect(() => {
    loadProducts();
  }, [filterCategory, filterActive, showLowStock]);

  const isLowStock = (product: Product) => product.quantity <= product.min_stock;
  const isOutOfStock = (product: Product) => product.quantity === 0;

  const getStockStatus = (product: Product) => {
    if (isOutOfStock(product)) return { text: 'Sem Estoque', color: '#ef4444' };
    if (isLowStock(product)) return { text: 'Estoque Baixo', color: '#f59e0b' };
    return { text: 'Estoque OK', color: '#10b981' };
  };

  const lowStockCount = products.filter(p => isLowStock(p) && p.is_active).length;

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>
          {t('products.title') || 'Gestão de Produtos'}
        </h1>
        <p style={{ color: '#666', margin: 0 }}>
          {t('products.subtitle') || 'Controle de estoque de produtos de limpeza'}
        </p>
      </div>

      {/* Alert para estoque baixo */}
      {lowStockCount > 0 && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
          <span style={{ color: '#92400e', fontWeight: '500' }}>
            {lowStockCount} produto(s) com estoque baixo!
          </span>
          <button
            onClick={() => setShowLowStock(!showLowStock)}
            style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            {showLowStock ? 'Ver Todos' : 'Ver Apenas Baixo Estoque'}
          </button>
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        {/* Filtros */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              {t('products.filter.category') || 'Categoria'}
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">{t('products.filter.allCategories') || 'Todas as categorias'}</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              {t('products.filter.status') || 'Status'}
            </label>
            <select
              value={filterActive === null ? '' : filterActive.toString()}
              onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">{t('products.filter.allStatus') || 'Todos'}</option>
              <option value="true">{t('products.filter.active') || 'Ativos'}</option>
              <option value="false">{t('products.filter.inactive') || 'Inativos'}</option>
            </select>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            {t('products.list') || 'Lista de Produtos'} ({products.length})
          </h3>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500'
            }}
          >
            <Plus size={20} />
            {t('products.new') || 'Novo Produto'}
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('products.loading') || 'Carregando produtos...'}</div>
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <Package size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
            <div>{t('products.notFound') || 'Nenhum produto encontrado'}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' }}>
            {products.map((product) => {
              const stockStatus = getStockStatus(product);
              const stockPercentage = product.min_stock > 0 
                ? Math.min((product.quantity / product.min_stock) * 100, 100) 
                : 100;
              
              return (
                <div key={product.id} style={{
                  border: `2px solid ${isLowStock(product) && product.is_active ? '#f59e0b' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: isLowStock(product) && product.is_active ? '#fffbeb' : 'white',
                  transition: 'all 0.2s'
                }}>
                  {/* Header */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                      <h4 style={{ margin: 0, color: '#333', fontSize: '16px', flex: 1 }}>
                        {product.name}
                      </h4>
                      <span style={{
                        backgroundColor: product.is_active ? '#d1fae5' : '#fee2e2',
                        color: product.is_active ? '#065f46' : '#991b1b',
                        padding: '3px 8px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '600',
                        marginLeft: '8px'
                      }}>
                        {product.is_active ? 'ATIVO' : 'INATIVO'}
                      </span>
                    </div>
                    {product.brand && (
                      <div style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
                        {product.brand}
                      </div>
                    )}
                    <div style={{
                      backgroundColor: '#f3f4f6',
                      color: '#4b5563',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      display: 'inline-block',
                      marginTop: '4px'
                    }}>
                      {product.category}
                    </div>
                  </div>

                  {/* Stock Indicator */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#666' }}>
                        Estoque:
                      </span>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '700',
                        color: stockStatus.color
                      }}>
                        {product.quantity} {product.unit}
                      </span>
                    </div>
                    
                    {/* Progress bar */}
                    <div style={{
                      height: '6px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '4px'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${stockPercentage}%`,
                        backgroundColor: stockStatus.color,
                        transition: 'all 0.3s'
                      }} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', color: '#999' }}>
                        Mín: {product.min_stock} {product.unit}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: stockStatus.color }}>
                        {stockStatus.text}
                      </span>
                    </div>
                  </div>

                  {/* Price */}
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <DollarSign size={14} style={{ color: '#10b981' }} />
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      Custo: <span style={{ fontWeight: '600', color: '#333' }}>
                        {formatCurrency(product.cost_price)}
                      </span>
                    </span>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => openStockModal(product)}
                      style={{
                        flex: 1,
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        padding: '6px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                      title="Ajustar Estoque"
                    >
                      📦 Estoque
                    </button>
                    <button
                      onClick={() => startEdit(product)}
                      style={{
                        flex: 1,
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '6px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      <Edit size={12} style={{ display: 'inline', marginBottom: '-2px' }} />{t('actions.edit')}</button>
                    {product.is_active && (
                      <button
                        onClick={() => openDeleteModal(product)}
                        style={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title={t('products.deactivate')}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Formulário */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
              {editingProduct 
                ? (t('products.edit') || 'Editar Produto') 
                : (t('products.new') || 'Novo Produto')}
            </h2>

            <form onSubmit={editingProduct ? updateProduct : createProduct}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('products.name') || 'Nome do Produto'} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Desinfetante Multiuso"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('products.brand') || 'Marca'}
                </label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="Ex: Veja, Ypê, etc"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('products.description') || 'Descrição'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder={t('products.descriptionPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    {t('products.category') || 'Categoria'} *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    {t('products.unit') || 'Unidade'} *
                  </label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    {UNITS.map(unit => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    {t('products.quantity') || 'Quantidade'} *
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    {t('products.minStock') || 'Estoque Mín'} *
                  </label>
                  <input
                    type="number"
                    value={formData.min_stock}
                    onChange={(e) => setFormData({ ...formData, min_stock: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    {t('products.costPrice') || 'Preço Custo'}
                  </label>
                  <input
                    type="number"
                    value={formData.cost_price}
                    onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    color: '#333'
                  }}
                >
                  {t('cancel') || 'Cancelar'}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading 
                    ? (t('saving') || 'Salvando...') 
                    : (t('save') || 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Ajuste de Estoque */}
      {showStockModal && adjustingProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>
              Ajustar Estoque
            </h2>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Produto:</div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>{adjustingProduct.name}</div>
              <div style={{ fontSize: '13px', color: '#999', marginTop: '3px' }}>
                Estoque Atual: <span style={{ fontWeight: '600', color: getStockStatus(adjustingProduct).color }}>
                  {adjustingProduct.quantity} {adjustingProduct.unit}
                </span>
              </div>
            </div>

            <form onSubmit={adjustStock}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Tipo de Ajuste *
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <label style={{ 
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    border: `2px solid ${stockAdjustment.type === 'add' ? '#10b981' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: stockAdjustment.type === 'add' ? '#d1fae5' : 'white'
                  }}>
                    <input
                      type="radio"
                      value="add"
                      checked={stockAdjustment.type === 'add'}
                      onChange={(e) => setStockAdjustment({ ...stockAdjustment, type: e.target.value })}
                      style={{ cursor: 'pointer' }}
                    />
                    <TrendingUp size={18} style={{ color: '#10b981' }} />
                    <span style={{ fontWeight: '500' }}>{t('actions.add')}</span>
                  </label>

                  <label style={{ 
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    border: `2px solid ${stockAdjustment.type === 'subtract' ? '#ef4444' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: stockAdjustment.type === 'subtract' ? '#fee2e2' : 'white'
                  }}>
                    <input
                      type="radio"
                      value="subtract"
                      checked={stockAdjustment.type === 'subtract'}
                      onChange={(e) => setStockAdjustment({ ...stockAdjustment, type: e.target.value })}
                      style={{ cursor: 'pointer' }}
                    />
                    <TrendingDown size={18} style={{ color: '#ef4444' }} />
                    <span style={{ fontWeight: '500' }}>{t('actions.remove')}</span>
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Quantidade ({adjustingProduct.unit}) *
                </label>
                <input
                  type="number"
                  value={stockAdjustment.quantity}
                  onChange={(e) => setStockAdjustment({ ...stockAdjustment, quantity: parseFloat(e.target.value) || 0 })}
                  required
                  min="0.01"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Motivo
                </label>
                <input
                  type="text"
                  value={stockAdjustment.reason}
                  onChange={(e) => setStockAdjustment({ ...stockAdjustment, reason: e.target.value })}
                  placeholder="Ex: Compra, Uso em serviço, Perda, etc"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Preview */}
              <div style={{
                backgroundColor: '#f9fafb',
                padding: '15px',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Estoque após ajuste:</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#333' }}>
                  {stockAdjustment.type === 'add' 
                    ? (adjustingProduct.quantity + stockAdjustment.quantity).toFixed(2)
                    : (adjustingProduct.quantity - stockAdjustment.quantity).toFixed(2)
                  } {adjustingProduct.unit}
                </div>
                {stockAdjustment.type === 'subtract' && stockAdjustment.quantity > adjustingProduct.quantity && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '5px' }}>
                    ⚠️ Quantidade maior que estoque atual!
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowStockModal(false); setAdjustingProduct(null); }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    color: '#333'
                  }}
                >
                  {t('cancel') || 'Cancelar'}
                </button>
                <button
                  type="submit"
                  disabled={isLoading || (stockAdjustment.type === 'subtract' && stockAdjustment.quantity > adjustingProduct.quantity)}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (isLoading || (stockAdjustment.type === 'subtract' && stockAdjustment.quantity > adjustingProduct.quantity)) ? 'not-allowed' : 'pointer',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    opacity: (isLoading || (stockAdjustment.type === 'subtract' && stockAdjustment.quantity > adjustingProduct.quantity)) ? 0.6 : 1
                  }}
                >
                  {isLoading 
                    ? (t('saving') || 'Salvando...') 
                    : (t('save') || 'Confirmar Ajuste')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Delete */}
      {showDeleteModal && deletingProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>{t('products.deleteTitle')}</h3>
            
            <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
              <strong>Produto:</strong> {deletingProduct.name}
            </p>
            
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
              Escolha como deseja deletar este produto:
            </p>

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '10px',
              marginBottom: '20px'
            }}>
              {/* Opção 1: Desativar (Soft Delete) */}
              <button
                onClick={softDeleteProduct}
                disabled={isLoading}
                style={{
                  padding: '15px',
                  border: '2px solid #f59e0b',
                  borderRadius: '6px',
                  backgroundColor: '#fffbeb',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  opacity: isLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fef3c7';
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fffbeb';
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#92400e' }}>
                  ⚠️ Desativar (Recomendado)
                </div>
                <div style={{ fontSize: '13px', color: '#78350f' }}>
                  O produto ficará oculto mas pode ser reativado a qualquer momento mudando o filtro de status para "Inativos".
                </div>
              </button>

              {/* Opção 2: Deletar Permanentemente (Hard Delete) */}
              <button
                onClick={hardDeleteProduct}
                disabled={isLoading}
                style={{
                  padding: '15px',
                  border: '2px solid #ef4444',
                  borderRadius: '6px',
                  backgroundColor: '#fef2f2',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  opacity: isLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fee2e2';
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fef2f2';
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#991b1b' }}>
                  🗑️ Deletar Permanentemente
                </div>
                <div style={{ fontSize: '13px', color: '#7f1d1d' }}>
                  <strong>{t('products.warning')}</strong> O produto será removido permanentemente do banco de dados e NÃO poderá ser recuperado!
                </div>
              </button>
            </div>

            {/* Botão Cancelar */}
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingProduct(null);
              }}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading ? 0.6 : 1
              }}
            >{t('actions.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Produtos;

